import { useMemo } from "react";
import type { IntegrationRun } from "../../../shared/types";
import { statusTone } from "../../utils/status";

export type GanttGroupBy = "JOB" | "SOURCE_TYPE";

interface RunsGanttChartProps {
  runs: IntegrationRun[];
  groupBy?: GanttGroupBy;
  onOpenJob: (jobId: string) => void;
}

interface GanttBar {
  run: IntegrationRun;
  left: number;
  width: number;
  startMs: number;
}

interface GanttRow {
  key: string;
  label: string;
  jobId: string;
  bars: GanttBar[];
}

const LABEL_WIDTH = 160;
const CHART_HEIGHT_MAX = 320;
const TICK_COUNT = 6;

function getGroupLabel(run: IntegrationRun, groupBy: GanttGroupBy): string {
  if (groupBy === "SOURCE_TYPE") {
    return run.sourceType;
  }

  return run.jobName || run.jobId;
}

function buildTicks(minMs: number, maxMs: number, count: number): { ms: number; pct: number }[] {
  const span = maxMs - minMs || 1;
  return Array.from({ length: count }, (_, i) => {
    const ms = minMs + (span / (count - 1)) * i;
    return { ms, pct: ((ms - minMs) / span) * 100 };
  });
}

export function RunsGanttChart({ runs, groupBy = "JOB", onOpenJob }: RunsGanttChartProps) {
  const { rows, minMs, maxMs } = useMemo(() => {
    const valid = runs.filter((run) => run.actualStartTime || run.expectedStartTime);
    if (valid.length === 0) {
      return { rows: [], minMs: 0, maxMs: 0 };
    }

    const runRanges = valid.map((run) => {
      const startMs = new Date(run.actualStartTime ?? run.expectedStartTime).getTime();
      const endMs = run.endTime
        ? new Date(run.endTime).getTime()
        : startMs + (run.durationMinutes ?? 5) * 60_000;
      return { run, startMs, endMs };
    });

    const minMs = Math.min(...runRanges.map(({ startMs }) => startMs));
    const maxMs = Math.max(...runRanges.map(({ endMs }) => endMs));
    const span = maxMs - minMs || 1;
    const groupedRows = new Map<string, GanttRow>();

    runRanges.forEach(({ run, startMs, endMs }) => {
      const label = getGroupLabel(run, groupBy);
      const key = `${groupBy}:${label.trim().toLowerCase()}`;
      const row = groupedRows.get(key) ?? {
        key,
        label,
        jobId: run.jobId,
        bars: []
      };

      row.bars.push({
        run,
        startMs,
        left: ((startMs - minMs) / span) * 100,
        width: Math.max(((endMs - startMs) / span) * 100, 0.4)
      });
      groupedRows.set(key, row);
    });

    const rows = Array.from(groupedRows.values()).map((row) => ({
      ...row,
      bars: row.bars.sort((a, b) => a.startMs - b.startMs)
    }));

    return { rows, minMs, maxMs };
  }, [groupBy, runs]);

  const ticks = useMemo(
    () => (minMs < maxMs ? buildTicks(minMs, maxMs, TICK_COUNT) : []),
    [minMs, maxMs]
  );

  if (rows.length === 0) {
    return null;
  }

  const spanMs = maxMs - minMs;
  const formatTick = (ms: number): string => {
    if (spanMs >= 24 * 60 * 60_000) {
      return new Intl.DateTimeFormat("en", {
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      }).format(new Date(ms));
    }

    return new Intl.DateTimeFormat("en", {
      hour: "2-digit",
      minute: "2-digit"
    }).format(new Date(ms));
  };

  return (
    <section className="panel gantt-panel">
      <div className="gantt-axis-bar">
        <div className="gantt-axis-label-spacer" style={{ width: LABEL_WIDTH }} />
        <div className="gantt-axis-ticks">
          {ticks.map((tick) => (
            <span
              key={tick.ms}
              className="gantt-axis-tick"
              style={{ left: `${tick.pct}%` }}
            >
              {formatTick(tick.ms)}
            </span>
          ))}
        </div>
      </div>

      <div className="gantt-scroll" style={{ maxHeight: CHART_HEIGHT_MAX }}>
        <div className="gantt-body">
          {rows.map((row) => (
            <div
              key={row.key}
              className="gantt-row"
              onClick={() => onOpenJob(row.jobId)}
              title={`${row.label} - ${row.bars.length} runs`}
              aria-label={`${row.label} Gantt row with ${row.bars.length} runs`}
              tabIndex={0}
              onKeyDown={(event) => event.key === "Enter" && onOpenJob(row.jobId)}
            >
              <div className="gantt-label" style={{ width: LABEL_WIDTH }}>
                {row.label}
              </div>
              <div className="gantt-track">
                {ticks.map((tick) => (
                  <div
                    key={tick.ms}
                    className="gantt-grid-line"
                    style={{ left: `${tick.pct}%` }}
                  />
                ))}
                {row.bars.map(({ run, left, width }) => (
                  <div
                    key={run.id}
                    className={`gantt-bar tone-${statusTone[run.status]}`}
                    style={{ left: `${left}%`, width: `${width}%` }}
                    title={`${run.jobName} - ${run.sourceName} - ${run.status}`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
